//#region IMPORTS
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import PaginationBarClientButton from "./PaginationBarClientButton";
import { STICKY_TOP_TABLE_CLASS } from "../_layout/stickyOffsets";
//#endregion

// Page sizes for client-side paginated lists (the workflow presets / export mode
// on /orders, where the whole result set is fetched at once). Default 50, the
// same page size as the server-side paginated overviews, so switching between
// a preset and an overview doesn't change how much you see per page.
export const CLIENT_PAGE_SIZE_DEFAULT = 50;
export const CLIENT_PAGE_SIZE_OPTIONS = [8, 10, 15, 20, 25, 30, 50];

//#region INTERFACE
interface Props {
  changePageSizeHandler?: (pageSize: number) => void;
  prevPageAvailable?: boolean;
  nextPageAvailable?: boolean;
  firstPageHandler?: () => void;
  prevPageHandler?: () => void;
  nextPageHandler?: () => void;
  lastPageHandler?: () => void;
  totalAmmount: number;
  currentPage?: number;
  pageCount?: number;
  pageSize?: number;
  disabled?: boolean;
  type?: string;
  hide?: boolean;
}
//#endregion

export default function PaginationBarClient({
  changePageSizeHandler,
  prevPageAvailable = false,
  nextPageAvailable = false,
  firstPageHandler,
  prevPageHandler,
  nextPageHandler,
  lastPageHandler,
  totalAmmount,
  currentPage = 0,
  pageSize = CLIENT_PAGE_SIZE_DEFAULT,
  pageCount = 0,
  disabled = false,
  type = "items",
  hide = false,
}: Props) {
  //#region RENDER

  if (hide || totalAmmount <= pageSize) return null;

  // Rendered once, above the table (see OrdersTable / InstoreOrdersTable), as
  // part of the sticky stack right under the filter bar (stickyOffsets.ts).
  return (
    <header
      className={`sticky z-10 bg-white pt-1 pb-2 ${STICKY_TOP_TABLE_CLASS} flex items-center justify-between gap-4 text-sm text-gray-500 px-4 sm:px-6 lg:px-8`}
    >
      <section className="flex items-center gap-4">
        {firstPageHandler && (
          <PaginationBarClientButton
            needHelpMessage="Go to first page"
            clickHandler={firstPageHandler}
            disabled={disabled || !prevPageAvailable}
          >
            <ArrowUturnLeftIcon className="w-5 h-5" />
          </PaginationBarClientButton>
        )}
        {prevPageHandler && (
          <PaginationBarClientButton
            needHelpMessage="Go to previous page"
            clickHandler={prevPageHandler}
            disabled={disabled || !prevPageAvailable}
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </PaginationBarClientButton>
        )}

        {nextPageHandler && (
          <PaginationBarClientButton
            needHelpMessage="Go to next page"
            clickHandler={nextPageHandler}
            disabled={disabled || !nextPageAvailable}
          >
            <ArrowRightIcon className="w-5 h-5" />
          </PaginationBarClientButton>
        )}

        {lastPageHandler && (
          <PaginationBarClientButton
            needHelpMessage="Go to last page"
            clickHandler={lastPageHandler}
            disabled={disabled || !nextPageAvailable}
          >
            <ArrowUturnRightIcon className="w-5 h-5" />
          </PaginationBarClientButton>
        )}

        <div className="flex items-center gap-1">
          <span>page</span>
          <strong>
            {currentPage} of {pageCount}
          </strong>
        </div>
      </section>

      <section className="flex items-center">
        {changePageSizeHandler ? (
          <Fragment>
            <select
              disabled={disabled}
              className="block border-0 py-1 text-rose-800 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              value={pageSize}
              onChange={(e) => {
                changePageSizeHandler(Number(e.target.value));
              }}
            >
              {CLIENT_PAGE_SIZE_OPTIONS.map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
            <span>
              of {totalAmmount} {type}
            </span>
          </Fragment>
        ) : (
          <div>
            {totalAmmount} {type}
          </div>
        )}
      </section>
    </header>
  );
  //#endregion
}
